/**
 * uploadLimit.js — PROD-228 (patient half, 2026-10-09; rc2: the Lambda request quota, with
 * PROD-791). WHETHER A FILE'S REQUEST FITS THE API, decided before anything is sent.
 *
 * ★★★ WHY. The production HTTP API has no CorsConfiguration. A request AWS refuses before
 *   orella-api runs is answered without access-control-allow-origin (measured 2026-10-08 in TEST:
 *   an 11 MB POST got HTTP 413 with no CORS header). A browser reports that as a network error,
 *   so a screen can only show its generic failure. The app must refuse such a file itself.
 *
 * ★★★ THE BINDING LIMIT IS LAMBDA'S, NOT THE GATEWAY'S. The route is API Gateway HTTP API ->
 *   AWS_PROXY Lambda (payload 2.0) -> Mangum -> Flask. The gateway takes 10 MB of body, but the
 *   whole invocation event a synchronous Lambda accepts is 6 MB (6,291,456 bytes), and the body
 *   travels inside that event (read from code by hints7, 2026-10-09; not measured live):
 *   · POST /ai/parse-medical-document is JSON carrying the file as a base64 data URL: the event
 *     holds that JSON as text, 4/3 of the file plus the JSON around it (OnboardingAutoFill,
 *     MedicalDocumentUpload, ImportMedicalRecordsScreen).
 *   · POST /cases/<id>/custom-doc/patient-upload is multipart: a binary body, so the event holds
 *     it base64-encoded, 4/3 of the multipart body (CustomDocHandler).
 *   REQUEST_BODY_MAX_BYTES (6,000,000) leaves 291,456 bytes of the quota for the rest of the
 *   event: headers, request context, and the body's own escaping inside the event JSON.
 *
 * ★★ The screens' own file checks are 4 MiB (PROD-791, Alex 2026-10-09 "max 4 MB"). A 4 MiB file
 *   passes these bounds with room to spare (check-upload-size US-1 / US-2 / US-3), so for an
 *   ordinary file name the screen's 4 MB sentence is what a patient sees; this guard is the
 *   backstop that keeps a request under the quota whatever the file check says.
 *
 * All byte counts below are UPPER bounds of the real body / event body (check-upload-size US-1
 * compares them with the real serialisation): a file they let through always fits.
 *
 * The limits orella-api sets itself are all above the quota on these routes (MAX_CONTENT_LENGTH
 * 35 MB; CUSTOM_DOC_MAX_BYTES 20 MB; the parse route has no size check of its own), so the
 * Lambda quota is the binding one (check-upload-size US-4 reads the API checkout).
 *
 * The function names keep "Gateway": the request goes through the API Gateway -> Lambda path.
 *
 * Pure module, no imports: the gates execute it directly.
 */

// AWS Lambda's synchronous invocation request quota: 6 MB = 6,291,456 bytes for the whole event.
export const LAMBDA_REQUEST_QUOTA_BYTES = 6_291_456
// The most body bytes the event may carry: the quota minus a margin for the event around the body.
export const REQUEST_BODY_MAX_BYTES = 6_000_000

const sizeOf = (file) => (file && Number.isFinite(file.size) && file.size >= 0 ? file.size : null)
// A JSON-escaped or UTF-8 character is at most 6 bytes (\uXXXX); an emoji is two UTF-16 units.
const textBound = (s) => 6 * String(s || '').length

/**
 * Upper bound, in bytes, of JSON.stringify({ file: <data URL of file>, fileType, fileName }).
 * The data URL is "data:<type>;base64," + 4 * ceil(size / 3) characters; FileReader may write
 * application/octet-stream for an empty type, hence the 64 bytes reserved for the type.
 * The event carries this JSON as text, so this is also the body's size in the event.
 */
export function dataUrlJsonBodyBytes(file) {
  const size = sizeOf(file)
  if (size === null) return Infinity
  const type = String((file && file.type) || '')
  return 4 * Math.ceil(size / 3) + 2 * (64 + textBound(type)) + textBound(file && file.name) + 256
}

/** Upper bound, in bytes, of a multipart/form-data body carrying this one file. */
export function multipartBodyBytes(file) {
  const size = sizeOf(file)
  if (size === null) return Infinity
  // boundary lines, the part's Content-Disposition (name + filename, escaped) and Content-Type
  return size + 512 + 3 * textBound(file && file.name) + textBound(file && file.type)
}

/** Upper bound, in bytes, of that multipart body inside the Lambda event: base64, 4/3 of it. */
export function multipartEventBodyBytes(file) {
  const body = multipartBodyBytes(file)
  return Number.isFinite(body) ? 4 * Math.ceil(body / 3) : Infinity
}

export function fitsGatewayAsDataUrlJson(file) {
  return dataUrlJsonBodyBytes(file) <= REQUEST_BODY_MAX_BYTES
}

export function fitsGatewayAsMultipart(file) {
  return multipartEventBodyBytes(file) <= REQUEST_BODY_MAX_BYTES
}
